import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@ui/card.tsx';
import {
  RotateCw,
  Box,
  Users,
  ChevronRight,
  LucideShoppingCart,
  BoltIcon,
  Layers,
  Network,
  SlidersHorizontal,
  TrendingUp
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Separator } from '@ui/separator.tsx';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { useRoutes } from '@r/hooks/use-routes.ts';
import { linksAvailable } from '@r/utils/utils.ts';
import { isRenewableLogicType, isStepLogicType } from '@admin/types/workspace/logic.ts';
import { ProgramExpirationBanner } from '@admin/components/renew-program/program-expiration-banner.tsx';
import { WorkspaceMisconfiguredBanner } from '@admin/components/workspace-configuration/workspace-misconfigured-banner.tsx';
import { RenewalProgramDialogLayout } from '@admin/components/renew-program/renewal-program-dialog-layout.tsx';
import { canManagePurchaseProofSettings } from './purchase-proof-settings-utils.ts';

export const SettingsPage = () => {
  const { t } = useTranslation();
  const [isRenewalDialogOpen, setIsRenewalDialogOpen] = useState(false);
  const user = useUser().data;
  const { workspace } = UseWorkspace(user);
  const currentLogic = workspace?.currentLogicWorkspace;
  const showRenewalSection = Boolean(
    currentLogic && isRenewableLogicType(currentLogic.logic?.type)
  );
  const showStepsListEntry = Boolean(currentLogic && isStepLogicType(currentLogic.logic?.type));
  const hasActiveShops = (workspace?.activeShops ?? []).length > 0;
  const allRoutes = useRoutes();
  const activeWorkspace = getActiveWorkspaceUniversal(user?.visibleWorkspaces ?? []);
  // Gestion de la hiérarchie commerciale réservée aux ADMIN.
  const isAdmin = Boolean(activeWorkspace?.roles?.includes('ROLE_ADMIN'));
  const canManageFeatures = canManagePurchaseProofSettings(activeWorkspace?.roles ?? []);

  const allOptions = [
    {
      title: t('menu.admin.scheduled.tasks'),
      icon: RotateCw,
      url: '/settings/scheduled-tasks'
    },
    {
      title: t('menu.admin.api'),
      icon: Box,
      url: '/settings/api/tokens'
    },
    ...(canManageFeatures
      ? [
          {
            title: t('menu.admin.features'),
            icon: SlidersHorizontal,
            url: '/settings/features'
          }
        ]
      : []),
    {
      title: t('menu.admin.typologies'),
      icon: Users,
      url: '/settings/typologies'
    },
    {
      title: t('menu.admin.segments'),
      icon: Layers,
      url: '/settings/segments'
    },
    ...(isAdmin
      ? [
          {
            title: t('menu.admin.hierarchy'),
            icon: Network,
            url: '/settings/hierarchy'
          }
        ]
      : []),
    {
      title: t('workspace.profile'),
      icon: BoltIcon,
      url: '/settings/my-workspace'
    },
    ...(hasActiveShops
      ? [
          {
            title: t('menu.admin.shops.settings'),
            icon: LucideShoppingCart,
            url: '/settings/shops/settings'
          }
        ]
      : []),
    ...(showStepsListEntry
      ? [
          {
            title: t('menu.admin.steps.list'),
            icon: TrendingUp,
            url: '/settings/steps'
          }
        ]
      : [])
  ];

  const availableUrls = linksAvailable(
    allRoutes.data,
    allOptions.map((o) => o.url)
  );
  const settingsOptions = allOptions.filter((o) => availableUrls.includes(o.url));

  const handleRenewalClick = () => {
    setIsRenewalDialogOpen(true);
  };

  const queryClient = useQueryClient();

  const handleRenewalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['workspace'] });
  };

  return (
    <div className="flex flex-col space-y-10 grow">
      <div className="space-y-3">
        <h1 className="text-4xl font-bold">{t('menu.admin.configuration')}</h1>
        <p className="font-light text-muted-foreground">{t('settings.description')}</p>
      </div>
      <div className="flex flex-col gap-3 empty:hidden">
        <WorkspaceMisconfiguredBanner />
        {showRenewalSection && (
          <ProgramExpirationBanner
            buttonText={t('settings.renewal.banner.button.renew')}
            onButtonClick={handleRenewalClick}
          />
        )}
      </div>
      <Card className="w-full">
        <CardContent className="p-6">
          <h2 className="mb-6 text-xl font-semibold">{t('settings.configure.program')}</h2>
          <div className="space-y-0">
            {settingsOptions.map((option, index) => {
              const Icon = option.icon;
              const isLast = index === settingsOptions.length - 1;
              return (
                <div key={option.url}>
                  <Link
                    to={option.url}
                    className="flex items-center py-4 w-full transition-colors hover:bg-muted/50"
                  >
                    <div className="flex flex-1 items-center space-x-4">
                      <Icon className="w-5 h-5 text-foreground" />
                      <span className="text-base font-medium">{option.title}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </Link>
                  {!isLast && <Separator />}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      {showRenewalSection && (
        <Card className="w-full">
          <CardContent className="p-6">
            <h2 className="mb-6 text-xl font-semibold">{t('settings.renewal.section.title')}</h2>
            <div className="space-y-0">
              <button
                type="button"
                className="flex items-center py-4 w-full text-left transition-colors cursor-pointer hover:bg-muted/50"
                onClick={handleRenewalClick}
              >
                <div className="flex flex-1 items-center space-x-4">
                  <RotateCw className="w-5 h-5 text-foreground shrink-0" />
                  <span className="text-base font-medium">
                    {t('settings.renewal.section.button')}
                  </span>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      <RenewalProgramDialogLayout
        open={isRenewalDialogOpen}
        onOpenChange={setIsRenewalDialogOpen}
        onSuccess={handleRenewalSuccess}
      />
    </div>
  );
};
